import {
    AnyAction,
    countPatchChanges,
    createActionRecord,
    InvalidActionRecordError,
    isDocumentAction,
    IUserData,
    patchFilter,
    UserPresenceStatus,
} from "@buildwithflux/core";
import {ClientFunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {IActionRecord, IDocumentData, DeprecatedTrackingEvents} from "@buildwithflux/models";
import {guid} from "@buildwithflux/shared";
import {diff} from "deep-diff";
import {debounce, keys, values} from "lodash";

import {FluxServices} from "export";
import {currentAgentIsBot} from "helpers/isBot";
import R from "resources/Namespace";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import {DocumentStorageHelper} from "modules/storage_engine/helpers/DocumentStorageHelper";
import {isBatchAction} from "modules/document/state/helpers/batchActions";

/**
 * Helper to extract the effective action from a batch action or regular action.
 * For batch actions, finds the first sub-action with shouldGenerateActionRecord: true.
 * For regular actions, returns the action itself if it has shouldGenerateActionRecord: true.
 */
function getEffectiveActionForRecord(action: AnyAction): AnyAction | undefined {
    if (isBatchAction(action)) {
        // For batch actions, find the first sub-action with shouldGenerateActionRecord: true
        for (const subAction of action.payload) {
            if (isDocumentAction(subAction) && subAction.payload?.shouldGenerateActionRecord) {
                return subAction;
            }
        }
        return undefined;
    }
    // For regular actions, return the action if it has shouldGenerateActionRecord: true
    if (action.payload?.shouldGenerateActionRecord) {
        return action;
    }
    return undefined;
}

/**
 * @see createOriginActionRecord for the special way of creating an initial
 * action record without an action
 */
export function createActionRecordFromActionAndDocument(
    currentUser: IUserData,
    action: AnyAction,
    newDocumentState: IDocumentData,
): IActionRecord {
    // For batch actions, find the first sub-action with shouldGenerateActionRecord: true
    // This is needed because batch actions have an array payload, not an object with shouldGenerateActionRecord
    const effectiveAction = getEffectiveActionForRecord(action);
    if (!effectiveAction) {
        throw new InvalidActionRecordError(
            `Action ${action.type} does not have shouldGenerateActionRecord true, so it should not be used to create an action record.`,
        );
    }
    if (!newDocumentState.updated_at) {
        throw new InvalidActionRecordError(`Document has empty updated_at field."`);
    }
    if (!currentUser) {
        throw new InvalidActionRecordError(
            `Cannot create an action record for action - no current user.  Action: ${JSON.stringify(action)} `,
        );
    }
    // Check if it is a markdown doc action which doesn't use patches
    const isMarkdownAction =
        effectiveAction.type === "createProjectDoc" ||
        effectiveAction.type === "updateProjectDoc" ||
        effectiveAction.type === "deleteProjectDoc";

    if (isMarkdownAction) {
        // Markdown actions don't use patches, so we build a legacy diff
        const diff = buildMarkdownDocLegacyDiff(effectiveAction);
        return createActionRecord({
            changeData: diff as any, // legacy diff array
            name: effectiveAction.type,
            actingUserUid: currentUser?.uid,
            actingUserHandle: currentUser?.handle,
            timestamp: effectiveAction.payload.updatesDocumentTimestamp ? newDocumentState.updated_at : Date.now(),
            changedDocumentUid: newDocumentState.uid,
            actionUpdatesDocumentVersion: effectiveAction.payload.updatesDocumentTimestamp,
        });
    }

    const latestPatch = newDocumentState.latestPatch;
    if (!latestPatch || countPatchChanges(latestPatch) === 0) {
        throw new InvalidActionRecordError('Action record diff with name "' + action.type + '" is empty."');
    }
    if (latestPatch.uid !== newDocumentState.latestActionRecordUid) {
        throw new InvalidActionRecordError(
            `Document latestPatch has a different ID ${latestPatch.uid} than the latestActionRecordUid ${newDocumentState.latestActionRecordUid}. Was the patch applied?`,
        );
    }

    return createActionRecord({
        changeData: {
            uid: latestPatch.uid,
            type: latestPatch.type,
            // HACK: filter out `selectedObjectUids` from patch paths... maybe
            // this should be handled by the subscriber? especially considering
            // that we have special code in change history to re-select objects
            // affected by a patch, lol!
            // TODO: FLUX-5058 move filter of `selectedObjectUids` from patch paths to subscriber
            forward: latestPatch.forward.filter(patchFilter),
            reverse: latestPatch.reverse.filter(patchFilter),
            previousUid: latestPatch.previousUid,
        },

        // Use effectiveAction.type for better clarity in action record history
        // For batch actions, this will be the type of the first sub-action with shouldGenerateActionRecord
        name: effectiveAction.type,
        actingUserUid: currentUser?.uid,
        actingUserHandle: currentUser?.handle,
        // Use effectiveAction for payload properties since batch actions have array payloads
        timestamp: effectiveAction.updatesDocumentTimestamp ? newDocumentState.updated_at : Date.now(),
        changedDocumentUid: newDocumentState.uid,
        actionUpdatesDocumentVersion: effectiveAction.payload.updatesDocumentTimestamp,
    });
}

/**
 * Builds a legacy deep-diff for markdown doc actions.
 * @see PRD: Versioned Change History for Markdown Docs
 */
function buildMarkdownDocLegacyDiff(action: AnyAction): any[] {
    const {projectDocUid, name, content, previousContent} = action.payload;
    const path = ["markdownDocs", projectDocUid];
    const snapshot = {uid: projectDocUid, name, content};

    switch (action.type) {
        case "createProjectDoc":
            return [{kind: "N", path, rhs: snapshot}];
        case "deleteProjectDoc":
            return [{kind: "D", path, lhs: snapshot}];
        case "updateProjectDoc":
            return [
                {
                    kind: "E",
                    path,
                    lhs: {uid: projectDocUid, name, content: previousContent},
                    rhs: snapshot,
                },
            ];
        default:
            return [];
    }
}

export function queueActionRecord(
    currentUser: IUserData,
    action: AnyAction,
    newDocumentState: IDocumentData,
    services: FluxServices,
) {
    const actionRecord = createActionRecordFromActionAndDocument(currentUser, action, newDocumentState);

    // NOTE: the write is done async but the data prep is still expensive
    void writeActionRecord(actionRecord, newDocumentState, services);

    return actionRecord.actionRecordUid;
}

async function writeActionRecord(
    actionRecord: IActionRecord,
    documentData: IDocumentData,
    services: FluxServices,
): Promise<void> {
    const {actionRecordRepository, analyticsStorage, functionsAdapter} = services;

    try {
        await actionRecordRepository.save(actionRecord.actionDocumentUid, actionRecord);
    } catch (error) {
        if (error instanceof Error) {
            const newError = new Error(
                `Error saving action record ${actionRecord.actionName} ${actionRecord.actionDocumentUid}: ${error.message}`,
            );
            newError.cause = error;
            FluxLogger.captureError(newError);
        }
        return;
    }

    // Add action record to change history
    // QUESTION: why don't we just rely on
    // subscribeToNewActionRecordsForDocumentContext to update the local change
    // history? is it because we filter out the current clientId from that query?
    services.changeHistoryStore.getState().addChangeHistoryRecords({
        [actionRecord.actionRecordUid]: actionRecord,
    });

    void writeUserPresenceDebounced(
        actionRecord.actionUserUid,
        actionRecord.actionDocumentUid,
        functionsAdapter,
        documentData,
    ).catch((error) => {
        // NOTE: we don't want an error here to stop autosave
        if (error instanceof Error) {
            error.message = `Error writing user presence for action record ${actionRecord.actionRecordUid}: ${error.message}`;
        }
        FluxLogger.captureError(error);
    });

    void analyticsStorage
        .logEvent(DeprecatedTrackingEvents.updateDocument, {
            content_type: "document",
            content_id: documentData.uid,
            action_type: actionRecord.actionName,
            document_uid: actionRecord.actionDocumentUid,
            document_name: documentData.name,
            document_description: documentData.description,
            document_archived: documentData.archived,
            document_owner_uid: documentData.owner_uid,
            document_slug: documentData.slug,
            document_copy_of_document_uid: documentData.copy_of_document_uid,
            document_belongs_to_part_uid: documentData.belongs_to_part_uid,
            document_active_users: keys(documentData.active_users).length,
            document_roles: keys(documentData.roles).length,
            document_elements_count: keys(documentData.elements).length,
            document_routes_count: keys(documentData.routes).length,
        })
        .catch((error) => {
            // NOTE: we don't want an error here to stop autosave
            if (error instanceof Error) {
                error.message = `Error logging event for action record ${actionRecord.actionRecordUid}: ${error.message}`;
            }
            FluxLogger.captureError(error);
        });
}

const writeUserPresenceDebounced = debounce(
    async (
        userUid: string,
        documentUid: string,
        functionsAdapter: ClientFunctionsAdapter,
        documentData: IDocumentData,
    ) => {
        if (!currentAgentIsBot) {
            await functionsAdapter.writeUserPresence({
                userUid: userUid,
                documentUid: documentUid,
                userStatus: UserPresenceStatus.engaging.toString(),
                timestamp: new Date().getTime(),
                userColor: DocumentStorageHelper.getUserColor(values(documentData.active_users ?? {})),
            });
        }
    },
    R.behaviors.storage.userPresence.writeDelay,
    // NOTE: it is important to have the leading and trailing options set to
    // true for best latency and recency
    {leading: true, trailing: true},
);

/**
 * Create the origin action record for a newly created document
 * @param newDocument - document newly created
 * @param actionName - name of the action record, please note that we need a description for this action record, stored in ActionDisplayString
 *
 * QUESTION: why do we even want origin action records? Currently
 * createDocument and forkDocument. They are filtered out from reverts.
 * And they still use the old patch format!
 *
 * @see createActionRecordFromActionAndDocument for the normal way to create action records
 *
 * QUESTION: instead of the document created_at timestamp, should we use the
 * document updated_at timestamp for consistency with createActionRecordFromActionAndDocument?
 */
export function createOriginActionRecord(
    newDocument: IDocumentData,
    actionName: string,
    useDocumentCreateTS: boolean,
): IActionRecord {
    return {
        actionRecordUid: guid(),
        actionPreviousUid: null,
        actionName,
        actionUserUid: newDocument.owner_uid,
        actionTimestamp: useDocumentCreateTS ? newDocument.created_at : new Date().getTime(),
        actionDocumentUid: newDocument.uid,
        actionChangeDiff: createDiffForOriginActionRecord(newDocument),
        actionUpdatesDocumentVersion: true,
    };
}

// QUESTION: shouldn't we update this to use the new action record immer-based patches?
function createDiffForOriginActionRecord(newDocument: IDocumentData): IActionRecord["actionChangeDiff"] {
    const changeSet = diff({}, newDocument);
    if (!changeSet)
        throw new InvalidActionRecordError(
            `Tried to create origin action record diff, but it was empty?  Document: ${JSON.stringify(newDocument)}`,
        );
    return changeSet;
}
